import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import toast from "react-hot-toast";
import server from "../../utils/serverConfig";

// Helper function to get symbol-specific multiplier and rounding unit
const getSymbolConfig = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  
  if (upperSymbol.includes('BANKNIFTY')) {
    return {
      multiplierField: 'banknifty_multiplier',
      roundingUnit: 35,
      symbolType: 'BANKNIFTY'
    };
  } else if (upperSymbol.includes('FINNIFTY')) {
    return {
      multiplierField: 'finnifty_multiplier',
      roundingUnit: 65,
      symbolType: 'FINNIFTY'
    };
  } else if (upperSymbol.includes('MIDCPNIFTY')) {
    return {
      multiplierField: 'midcpnifty_multiplier',
      roundingUnit: 20,
      symbolType: 'MIDCPNIFTY'
    };
  } else if (upperSymbol.includes('SENSEX')) {
    return {
      multiplierField: 'sensex_multiplier',
      roundingUnit: 20,
      symbolType: 'SENSEX'
    };
  } else if (upperSymbol.includes('NIFTY')) {
    return {
      multiplierField: 'index_multiplier',
      roundingUnit: 75,
      symbolType: 'NIFTY'
    };
  }
  
  return null;
};

// Helper function to get base quantity for index positions
const getIndexPositionBaseQuantity = (symbol, strategy, side, positionData = [], accountNames = []) => {
  const referenceClientId = "CA00246";
  const symbolConfig = getSymbolConfig(symbol);
  
  if (!symbolConfig) {
    console.log(`❌ Symbol "${symbol}" is not recognized as an index symbol`);
    return null;
  }
  
  console.log(`\n=== DEBUGGING ${symbolConfig.symbolType} POSITION BASE CALCULATION ===`);
  console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side}, Client="${referenceClientId}"`);
  console.log(`📊 Symbol Config: Type=${symbolConfig.symbolType}, MultiplierField=${symbolConfig.multiplierField}, RoundingUnit=${symbolConfig.roundingUnit}`);
  
  // Find reference client's multiplier from accountNames
  // console.log(`\n📋 Available Accounts:`);
  accountNames.forEach(u => {
    const multiplier = u[symbolConfig.multiplierField];
    console.log(`  - ID: "${u.client_id}", Name: "${u.name}", ${symbolConfig.symbolType} Multiplier: ${multiplier}`);
  });
  
  const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
  if (!referenceClientData) {
    console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
    console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
    return null;
  }
  
  const referenceMultiplier = referenceClientData[symbolConfig.multiplierField];
  if (!referenceMultiplier || referenceMultiplier <= 0) {
    console.log(`❌ Reference client "${referenceClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${referenceMultiplier}`);
    return null;
  }
  
  console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", ${symbolConfig.symbolType} Multiplier=${referenceMultiplier}`);
  
  // Show ALL positions for this symbol
  console.log(`\n📦 All positions for symbol "${symbol}":`);
  const symbolPositions = positionData.filter(p => p.symbol === symbol);
  symbolPositions.forEach((p, i) => {
    console.log(`  [${i}] Strategy="${p.strategy}", Side=${p.side}, NetQty=${p.netQty}, ClientIDs=[${p.clientIds.join(', ')}]`);
  });
  
  // Find reference client's position quantity for this EXACT symbol + strategy + side combination
  console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${side} + Client="${referenceClientId}"`);
  
  const referencePosition = positionData.find(position => 
    position.symbol === symbol && 
    position.strategy === strategy && 
    position.side === side && 
    position.clientIds.includes(referenceClientId)
  );
  
  if (!referencePosition) {
    console.log(`❌ NO EXACT MATCH FOUND for reference client "${referenceClientId}"!`);
    console.log(`📦 Positions matching symbol+strategy+side (without client filter):`);
    const partialMatches = positionData.filter(p => 
      p.symbol === symbol && p.strategy === strategy && p.side === side
    );
    partialMatches.forEach(p => {
      console.log(`  - NetQty=${p.netQty}, ClientIDs=[${p.clientIds.join(', ')}], Contains "${referenceClientId}"? ${p.clientIds.includes(referenceClientId)}`);
    });
    
    // 🔄 FALLBACK LOGIC: Use any position's netQty with that client's multiplier
    if (partialMatches.length > 0) {
      const fallbackPosition = partialMatches[0]; // Use first matching position
      const fallbackClientId = fallbackPosition.clientIds[0]; // Use first client from the position
      
      console.log(`🔄 FALLBACK: Using position netQty with client's multiplier`);
      console.log(`📦 Fallback position: NetQty=${fallbackPosition.netQty}, ClientIDs=[${fallbackPosition.clientIds.join(', ')}]`);
      console.log(`👤 Using fallback client: "${fallbackClientId}"`);
      
      // Find the fallback client's multiplier
      const fallbackClientData = accountNames.find(user => user.client_id === fallbackClientId);
      if (!fallbackClientData) {
        console.log(`❌ Fallback client "${fallbackClientId}" NOT FOUND in accountNames`);
        return null;
      }
      
      const fallbackMultiplier = fallbackClientData[symbolConfig.multiplierField];
      if (!fallbackMultiplier || fallbackMultiplier <= 0) {
        console.log(`❌ Fallback client "${fallbackClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${fallbackMultiplier}`);
        return null;
      }
      
      console.log(`✅ Found fallback client: ID="${fallbackClientData.client_id}", Name="${fallbackClientData.name}", ${symbolConfig.symbolType} Multiplier=${fallbackMultiplier}`);
      
      const fallbackQty = fallbackPosition.netQty;
      
      // Calculate base quantity using the fallback client's multiplier
      const rawFallbackBaseQty = fallbackQty / fallbackMultiplier;
      
      // Round the base quantity to nearest multiple of symbol-specific rounding unit
      const fallbackBaseQty = Math.round(rawFallbackBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
      
      console.log(`\n📊 FALLBACK CALCULATION:`);
      console.log(`   NetQty: ${fallbackQty}`);
      console.log(`   ${symbolConfig.symbolType} Multiplier (${fallbackClientId}): ${fallbackMultiplier}`);
      console.log(`   Raw Base: ${fallbackQty} ÷ ${fallbackMultiplier} = ${rawFallbackBaseQty}`);
      console.log(`   Rounded Base: ${rawFallbackBaseQty} → ${fallbackBaseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
      console.log(`🎉 FALLBACK BASE QUANTITY: ${fallbackBaseQty}`);
      console.log(`=== END ${symbolConfig.symbolType} POSITION DEBUGGING (FALLBACK) ===\n`);
      
      return fallbackBaseQty;
    }
    
    console.log(`🔄 NO FALLBACK POSSIBLE - No positions found for symbol+strategy+side combination`);
    return null;
  }
  
  console.log(`✅ FOUND EXACT MATCH!`);
  console.log(`📦 Position details: Strategy="${referencePosition.strategy}", Side=${referencePosition.side}, NetQty=${referencePosition.netQty}`);
  console.log(`👥 ClientIDs in this position: [${referencePosition.clientIds.join(', ')}]`);
  
  const referenceQty = referencePosition.netQty;
  
  // Calculate base quantity using the reference client's actual multiplier
  const rawBaseQty = referenceQty / referenceMultiplier;
  
  // Round the base quantity to nearest multiple of symbol-specific rounding unit
  const baseQty = Math.round(rawBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
  
  console.log(`\n📊 CALCULATION:`);
  console.log(`   NetQty: ${referenceQty}`);
  console.log(`   ${symbolConfig.symbolType} Multiplier: ${referenceMultiplier}`);
  console.log(`   Raw Base: ${referenceQty} ÷ ${referenceMultiplier} = ${rawBaseQty}`);
  console.log(`   Rounded Base: ${rawBaseQty} → ${baseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
  console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
  console.log(`=== END ${symbolConfig.symbolType} POSITION DEBUGGING ===\n`);
  
  return baseQty;
};

// Helper function to calculate expected quantity for any index symbol positions
const calculateIndexPositionExpectedQuantity = (baseQty, targetMultiplier, roundingUnit) => {
  // Calculate expected quantity
  const expectedQty = baseQty * targetMultiplier;
  
  // Round to nearest multiple of symbol-specific rounding unit
  const roundedQty = Math.round(expectedQty / roundingUnit) * roundingUnit;
  
  console.log(`Index position calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty} (unit: ${roundingUnit})`);
  
  return roundedQty;
};

// Helper function to check if symbol is index option
const isIndexOption = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY') || upperSymbol.includes('FINNIFTY') || upperSymbol.includes('SENSEX') || upperSymbol.includes('MIDCPNIFTY');
};


// Enhanced Modal Components
const DriftModal = ({isLoading, currentPositionData, handleCloseDriftModal, handlePositionDrift }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Drift Position</h3>
            <p className="text-sm text-zinc-400">Execute drift strategy</p>
          </div>
        </div>
        <button
          onClick={handleCloseDriftModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Side:</span>
              <p className={`font-medium ${currentPositionData?.side === 1 ? 'text-green-400' : 'text-red-400'}`}>
                {currentPositionData?.side === 1 ? 'BUY' : 'SELL'}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">{currentPositionData?.netQty}</p>
            </div>
          </div>
        </div>
        <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">
            ⚠️ This action will execute the drift strategy for this position. Please confirm to proceed.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseDriftModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionDrift}
          className={`disabled:cursor-not-allowed disabled:bg-violet-600/20 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors shadow-lg`}
        >
          Execute Drift
        </button>
      </div>
    </div>
  </div>
);

const TPModal = ({isLoading, currentPositionData, handleCloseTPModal, handlePositionTP }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Take Profit</h3>
            <p className="text-sm text-zinc-400">Execute take profit strategy</p>
          </div>
        </div>
        <button
          onClick={handleCloseTPModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Current P&L:</span>
              <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">LTP:</span>
              <p className="text-white font-medium">₹{currentPositionData?.ltp?.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
          <p className="text-green-200 text-sm">
            📈 This will execute the take profit strategy and potentially close profitable positions.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseTPModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionTP}
          className="disabled:cursor-not-allowed disabled:bg-amber-600/20 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Execute TP
        </button>
      </div>
    </div>
  </div>
);

const ExitPositionModal = ({isLoading, currentPositionData, handleCloseExitModal, handleExitPosition }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-rose-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Exit Position</h3>
            <p className="text-sm text-zinc-400">Close entire position</p>
          </div>
        </div>
        <button
          onClick={handleCloseExitModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">{currentPositionData?.netQty}</p>
            </div>
            <div>
              <span className="text-zinc-400">Final P&L:</span>
              <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
          <p className="text-red-200 text-sm">
            ⚠️ This action will close the entire position immediately. This action cannot be undone.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseExitModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
         disabled={isLoading}
          onClick={handleExitPosition}
          className="disabled:cursor-not-allowed disabled:bg-rose-600/20 px-6 py-2 bg-rose-600 hover:bg-rose-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Exit Position
        </button>
      </div>
    </div>
  </div>
);

export default function NewLivePositionsRestAccounts({
  filteredLivePositionsData = [],
  setFilteredLivePositionsFno,
  setFilteredLivePositionsEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  ...props
}) {
  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sideFilter, setSideFilter] = useState('all'); // New state for side filter
  const [showDriftModal, setShowDriftModal] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [currentPositionData, setCurrentPositionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  

  console.log("AccountNames data:", accountNames);
  console.log("Position symbol detection test:", filteredLivePositionsData.map(position => ({
    symbol: position.symbol,
    strategy: position.strategy,
    side: position.side,
    isIndex: isIndexOption(position.symbol),
    symbolConfig: getSymbolConfig(position.symbol),
    netQty: position.netQty
  })));

  // Group positions by symbol + strategy + side
  const groupedPositions = useMemo(() => {
    const groups = {};
    
    filteredLivePositionsData.forEach(position => {
      const key = `${position.symbol}-${position.strategy}-${position.side}`;
      
      if (!groups[key]) {
        groups[key] = {
          symbol: position.symbol,
          strategy: position.strategy,
          side: position.side,
          netAvg: position.netAvg,
          ltp: position.ltp,
          pl: position.pl,
          actualQuantities: {}, // Store actual quantities from positions
          clientPositionMap: {} // Map client to their actual quantities
        };
      }
      
      // Store actual quantities and client mappings
      position.clientIds.forEach(clientId => {
        if (!groups[key].clientPositionMap[clientId]) {
          groups[key].clientPositionMap[clientId] = [];
        }
        groups[key].clientPositionMap[clientId].push(position.netQty);
      });
      
      if (!groups[key].actualQuantities[position.netQty]) {
        groups[key].actualQuantities[position.netQty] = [];
      }
      groups[key].actualQuantities[position.netQty].push(...position.clientIds);
    });
    
    // Remove duplicates from clientPositionMap
    Object.values(groups).forEach(group => {
      Object.keys(group.clientPositionMap).forEach(clientId => {
        group.clientPositionMap[clientId] = [...new Set(group.clientPositionMap[clientId])];
      });
    });
    
    return Object.values(groups);
  }, [filteredLivePositionsData]);

  // Generate multiplier tiers - Dynamic multipliers for all index symbols
  const enhancedGroupedPositions = useMemo(() => {
    return groupedPositions.map(group => {
      const isIndex = isIndexOption(group.symbol);
      
      if (isIndex) {
        const symbolConfig = getSymbolConfig(group.symbol);
        
        if (!symbolConfig) {
          console.log(`❌ Unknown index symbol: ${group.symbol}`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `Unknown index symbol: ${group.symbol}`
          };
        }
        
        // Use unified base quantity calculation for all index symbols
        const baseQty = getIndexPositionBaseQuantity(group.symbol, group.strategy, group.side, filteredLivePositionsData, accountNames);
        
        if (!baseQty) {
          console.log(`Could not determine base quantity for ${group.symbol} + ${group.strategy} + ${group.side}`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `No valid base quantity found for ${group.symbol} + ${group.strategy} + ${group.side}`
          };
        }
        
        console.log(`Processing ${symbolConfig.symbolType} position ${group.symbol} (${group.strategy}, side ${group.side}) with CA00246 base quantity: ${baseQty}`);
        
        // Get unique multipliers from accountNames for this specific symbol type
        const uniqueMultipliers = [...new Set(
          accountNames
            .map(user => user[symbolConfig.multiplierField])
            .filter(multiplier => multiplier && multiplier > 0)
        )].sort((a, b) => a - b);
        
        if (uniqueMultipliers.length === 0) {
          console.log(`❌ No valid multipliers found for ${symbolConfig.symbolType}`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `No valid ${symbolConfig.symbolType} multipliers found`
          };
        }
        
        const multiplierTiers = uniqueMultipliers.map(multiplier => {
          // Calculate expected quantity using symbol-specific logic
          const expectedQty = calculateIndexPositionExpectedQuantity(baseQty, multiplier, symbolConfig.roundingUnit);
          
          // Get clients who actually have this quantity (or closest to it)
          const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
          
          // Also check for clients with quantities close to expected (within 10% tolerance for rounding)
          const tolerance = expectedQty * 0.1;
          const clientsWithSimilarQty = [];
          
          Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
            const actualQty = parseInt(qty);
            if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
              clientsWithSimilarQty.push(...clientIds);
            }
          });
          
          const allClientsWithPosition = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
          
          return {
            multiplier,
            quantity: expectedQty,
            expectedQuantity: expectedQty,
            allClients: accountNames.filter(user => user[symbolConfig.multiplierField] === multiplier),
            clientsWithPosition: allClientsWithPosition
          };
        });
        
        return {
          ...group,
          multiplierTiers,
          isIndexOption: true,
          baseQuantity: baseQty,
          symbolConfig: symbolConfig
        };
      } else {
        // For stock options, keep current logic
        const quantityTiers = Object.entries(group.actualQuantities)
          .map(([qty, clientIds]) => ({
            quantity: parseInt(qty),
            clientIds: [...new Set(clientIds)]
          }))
          .sort((a, b) => a.quantity - b.quantity);
          
        return {
          ...group,
          quantityTiers,
          isIndexOption: false
        };
      }
    });
  }, [groupedPositions, accountNames, filteredLivePositionsData]);

  console.log("Enhanced grouped positions (Dynamic multipliers for all index symbols):", enhancedGroupedPositions);

  // Search and sorting logic (Type 2 search - reorder, don't filter)
  const searchedAndSortedPositions = useMemo(() => {
    let result = [...enhancedGroupedPositions];
    
    // Apply side filter first
    if (sideFilter !== 'all') {
      result = result.filter(position => {
        if (sideFilter === 'buy') {
          return position.side === 1;
        } else if (sideFilter === 'sell') {
          return position.side !== 1;
        }
        return true;
      });
    }
    
    // Apply sorting if configured
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle different data types
        if (sortConfig.key === 'symbol') {
          aValue = aValue.replace("NSE:", "").toLowerCase();
          bValue = bValue.replace("NSE:", "").toLowerCase();
        } else if (sortConfig.key === 'side') {
          aValue = aValue === 1 ? 'buy' : 'sell';
          bValue = bValue === 1 ? 'buy' : 'sell';
        } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    // Then apply search reordering (matching items go to top while maintaining sort order within each group)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      
      // Separate matching and non-matching positions while preserving their sorted order
      const matchingPositions = result.filter(position => 
        position.symbol.toLowerCase().includes(searchLower)
      );
      const nonMatchingPositions = result.filter(position => 
        !position.symbol.toLowerCase().includes(searchLower)
      );
      
      // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
      result = [...matchingPositions, ...nonMatchingPositions];
    }
    
    return result;
  }, [enhancedGroupedPositions, searchTerm, sortConfig, sideFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSideFilter = (filter) => {
    setSideFilter(filter);
  };

  const getSideColor = (side) => {
    return side === 1 ? "text-green-400" : "text-red-400";
  };

  const getPLColor = (pl) => {
    const plValue = parseFloat(pl);
    if (plValue > 0) return "text-green-400";
    if (plValue < 0) return "text-red-400";
    return "text-white";
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
      <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
  };

  // Modal handlers
  const handleCloseDriftModal = () => setShowDriftModal(false);
  const handleCloseTPModal = () => setShowTPModal(false);
  const handleCloseExitModal = () => setShowExitModal(false);

  const handleOpenDriftModal = (position) => {
    setCurrentPositionData(position);
    setShowDriftModal(true);
  };

  const handleOpenTPModal = (position) => {
    setCurrentPositionData(position);
    setShowTPModal(true);
  };

  const handleOpenExitModal = (position) => {
    setCurrentPositionData(position);
    setShowExitModal(true);
  };

  const handlePositionDrift = () => {
    setIsLoading(true);
     if (currentPositionData !== null) {

    const clientIds = {};
    
    Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
      // Get the quantity (first element of the array)
      clientIds[clientId] = quantities[0];
    });
    
    // Create the restructured payload
    const payload = {
      symbol: currentPositionData.symbol,
      side: currentPositionData.side,
      netAvg: currentPositionData.netAvg,
      ltp: currentPositionData.ltp,
      strategy: currentPositionData.strategy,
      pl: currentPositionData.pl,
      clientIds: clientIds
    };
    
    console.log("Restructured Payload:", payload);
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}drift_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("Drift Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position Drifted Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position Drift Failed !!", {
               duration: 5000,
             });
           }
           handleCloseDriftModal();
           setIsLoading(false);
         })
         .catch((error) => {
           console.log("Error in position request:", error);
           handleCloseDriftModal();
           setIsLoading(false);
         });
     } else {
       alert("Please select a position for Drift");
       setIsLoading(false);
     }
   };
 
   const handlePositionTP = () => {
      setIsLoading(true);
     if (currentPositionData !== null) {

    // Convert clientPositionMap to clientIds object
    const clientIds = {};
    
    Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
      clientIds[clientId] = quantities[0];
    });
    
    // Create the restructured payload
    const payload = {
      symbol: currentPositionData.symbol,
      side: currentPositionData.side,
      netAvg: currentPositionData.netAvg,
      ltp: currentPositionData.ltp,
      strategy: currentPositionData.strategy,
      pl: currentPositionData.pl,
      clientIds: clientIds
    };
    
    console.log("Restructured Payload:", payload);
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}tp_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("TP Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position TP Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position TP Failed !!", {
               duration: 5000,
             });
           }
           handleCloseTPModal();
           setIsLoading(false);
         })
         .catch((error) => {
           console.log("Error in position request:", error);
           handleCloseTPModal();
           setIsLoading(false);
         });
     } else {
       alert("Please select a position for TP");
       setIsLoading(false);
     }
   };
 
   const handleExitPosition = () => {
     setIsLoading(true);
     if (currentPositionData !== null) {
     
    // Convert clientPositionMap to clientIds object
    const clientIds = {};
    
    Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
      // Get the quantity (first element of the array)
      clientIds[clientId] = quantities[0];
    });
    
    // Create the restructured payload
    const payload = {
      symbol: currentPositionData.symbol,
      side: currentPositionData.side,
      netAvg: currentPositionData.netAvg,
      ltp: currentPositionData.ltp,
      strategy: currentPositionData.strategy,
      pl: currentPositionData.pl,
      clientIds: clientIds
    };
    
    console.log("Restructured Payload:", payload);
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}exit_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("Exit Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position Exit Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position Exit Failed !!", {
               duration: 5000,
             });
           }
           handleCloseExitModal();
           setIsLoading(false);
           
         })
         .catch((error) => {
           console.log("Error in exit position request:", error);
           handleCloseExitModal();
           setIsLoading(false);
         });
     } else {
       alert("Please select a position for exit");
       setIsLoading(false);
     }
   };

  if (!filteredLivePositionsData || filteredLivePositionsData.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Live Position Found!!</div>
      </div>
    );
  }

    console.log("Final Positions ", searchedAndSortedPositions);

  // Check if we have original data but no filtered results
  if (searchedAndSortedPositions.length === 0) {
    const getFilterMessage = () => {
      if (sideFilter === 'buy') {
        return 'No BUY positions found';
      } else if (sideFilter === 'sell') {
        return 'No SELL positions found';
      } else if (searchTerm.trim()) {
        return `No positions found matching "${searchTerm}"`;
      }
      return 'No positions found';
    };



    return (
      <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
        {/* Search Bar */}
        <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by symbol..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm">Filter:</span>
              <button
                onClick={() => handleSideFilter('all')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                ALL
              </button>
              <button
                onClick={() => handleSideFilter('buy')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'buy'
                    ? 'bg-green-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                BUY
              </button>
              <button
                onClick={() => handleSideFilter('sell')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'sell'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                SELL
              </button>
            </div>
          </div>
        </div>

        {/* Empty State Message */}
        <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
          <div className="text-center">
            <div className="text-6xl mb-4">📭</div>
            <div className="text-lg font-medium mb-2">{getFilterMessage()}</div>
            <div className="text-sm text-zinc-500">
              {sideFilter !== 'all' || searchTerm.trim() ? 'Try adjusting your filters or search terms' : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
      {/* Search Bar */}
      <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by symbol..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 text-sm">Filter:</span>
            <button
              onClick={() => handleSideFilter('all')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              ALL
            </button>
            <button
              onClick={() => handleSideFilter('buy')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'buy'
                  ? 'bg-green-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              BUY
            </button>
            <button
              onClick={() => handleSideFilter('sell')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'sell'
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              SELL
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-10 gap-4 px-6 py-1 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center col-span-2 space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            {getSortIcon('symbol')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Buy/Sell</span>
            {getSortIcon('side')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('netQty')}
          >
            <span>Net Qty</span>
            {getSortIcon('netQty')}
          </button>

          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('netAvg')}
          >
            <span>Avg Price</span>
            {getSortIcon('netAvg')}
          </button>

          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('ltp')}
          >
            <span>LTP</span>
            {getSortIcon('ltp')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('strategy')}
          >
            <span>Strategy</span>
            {getSortIcon('strategy')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('pl')}
          >
            <span>Total P&L</span>
            {getSortIcon('pl')}
          </button>
          
          <div className="text-left col-span-2">Actions</div>
        </div>
      </div>

      {/* Scrollable Table Body - No Pagination, All Results Shown */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {searchedAndSortedPositions.sort((a, b) => {

                const aSell = a.side === -1 || a.side === 2;
                const bSell = b.side === -1 || b.side === 2;
                
                // Sell positions come first (return negative to prioritize)
                return bSell - aSell;
              }).map((position, index) => (
              <div key={index} className={` ${
                searchTerm && position.symbol.toLowerCase().includes(searchTerm.toLowerCase()) 
                  ? ' bg-[#07F2C7]/50' 
                  : 'bg-zinc-900'
              }`}>
                {/* Main Position Row */}
                <div className="grid grid-cols-10 gap-4 px-6 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
                  <div className="font-medium col-span-2">
                    {position.symbol}
                  </div>
                  <div className={`font-medium ${getSideColor(position.side)}`}>
                    {position.side === 1 ? "BUY" : "SELL"}
                  </div>
                  <div>{position.isIndexOption ? 
                    position.multiplierTiers?.reduce((sum, tier) => sum + (tier.clientsWithPosition.length * tier.quantity), 0) :
                    position.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
                  }</div>
                  <div>₹{position.netAvg?.toFixed(2)}</div>
                  <div>₹{position.ltp !== 'N/A' ? position.ltp?.toFixed(2) : 'N/A'}</div>
                  <div className="font-medium">{position.strategy}</div>
                  <div className={`font-medium ${getPLColor(position.pl)}`}>
                    ₹{parseFloat(position.pl)?.toFixed(2)}
                  </div>
                  <div className="flex items-center space-x-2 col-span-2">
                    <button
                      onClick={() => handleOpenDriftModal(position)}
                      className="bg-violet-800 cursor-pointer hover:bg-violet-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      Drift
                    </button>
                    <button
                      onClick={() => handleOpenTPModal(position)}
                      className="bg-amber-800 cursor-pointer hover:bg-amber-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      TP
                    </button>
                    <button
                      onClick={() => handleOpenExitModal(position)}
                      className="bg-rose-800 cursor-pointer hover:bg-rose-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      Exit
                    </button>
                  </div>
                </div>

                {/* Enhanced Quantity-Client Sub Table */}
                <div className="px-6 pb-4">
                  <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                    {/* {position.isIndexOption ? (
                      // Index Options - Dynamic multipliers for all index symbols
                      <>
                        <div className="bg-zinc-700 px-5 py-2">
                          <div className="flex gap-4 text-xs font-medium text-zinc-300">
                            <div className="min-w-[125px] flex items-start justify-start">
                              {position.symbolConfig ? `${position.symbolConfig.symbolType} Multiplier` : 'Index Multiplier'}
                            </div>
                            <div className="min-w-[125px] flex items-start justify-start">Expected Qty</div>
                            <div className="min-w-[140px] flex items-start justify-start">Clients</div>
                            {position.baseQuantity && position.symbolConfig && (
                              <div className="min-w-[180px] flex items-start justify-start text-amber-400">
                                {`Base: ${position.baseQuantity} (CA00246 + Strategy, Unit: ${position.symbolConfig.roundingUnit})`}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {position.multiplierTiers && position.multiplierTiers.length > 0 ? (
                            position.multiplierTiers.map((tier, tierIndex) => (
                              <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                                <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                  {tier.multiplier}
                                </div>
                                <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                  {tier.quantity}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {tier.allClients.map((user, userIndex) => {
                                    const hasPosition = tier.clientsWithPosition.includes(user.client_id);
                                    return (
                                      <span 
                                        key={userIndex}
                                        className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 
                                        ${
                                          hasPosition && position.side === 1 ? 'bg-green-800 border border-green-500/30' : position.side !== 1 ? 'bg-rose-900 border-rose-500/30' : 'bg-zinc-700'
                                        }
                                         ${tier.quantity < 0 ? 'bg-rose-900 border-rose-500/30' : 'bg-blue-500 border-blue-500/30'}

                                        
                                        `}
                                      >
                                        {hasPosition && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                        <span>{user.name}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-red-400">
                              {position.error || 'No multiplier tiers available'}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      // Stock Options - Current Logic
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
                            <div>Quantity</div>
                            <div>Clients</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {position.quantityTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {accountNames
                                  .filter(user => tier.clientIds.includes(user.client_id))
                                  .map((user, userIndex) => {
                                    const hasPosition = tier.clientIds.includes(user.client_id);
                                    return (
                                      <span 
                                        key={userIndex}
                                        className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                          hasPosition && position.side === 1 ? 'bg-green-900 border border-green-500/30' :  position.side !== 1 ? 'bg-rose-900 border-rose-500/30' :'bg-zinc-700'
                                        }
                                         ${tier.quantity < 0 ? 'bg-rose-900 border-rose-500/30' : ''}
                                        `}
                                      >
                                        {hasPosition && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                        <span>{user.name}</span>
                                      </span>
                                    );
                                  })
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )} */}



                    {position.isIndexOption ? (
                        // Index Options - Dynamic multipliers for all index symbols
                        <>
                          <div className="bg-zinc-700 px-5 py-2">
                            <div className="flex gap-4 text-xs font-medium text-zinc-300">
                              <div className="min-w-[125px] flex items-start justify-start">
                                {position.symbolConfig ? `${position.symbolConfig.symbolType} Multiplier` : 'Index Multiplier'}
                              </div>
                              <div className="min-w-[125px] flex items-start justify-start">Expected Qty</div>
                              <div className="min-w-[140px] flex items-start justify-start">Clients</div>
                              {position.baseQuantity && position.symbolConfig && (
                                <div className="min-w-[180px] flex items-start justify-start text-amber-400">
                                  {`Base: ${position.baseQuantity} (CA00246 + Strategy, Unit: ${position.symbolConfig.roundingUnit})`}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="divide-y divide-zinc-700">
                            {position.multiplierTiers && position.multiplierTiers.length > 0 ? (
                              position.multiplierTiers.map((tier, tierIndex) => (
                                <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                                  <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                    {tier.multiplier}
                                  </div>
                                  <div className={`font-medium ${tier.quantity < 0 ? 'text-rose-400' : 'text-emerald-400'} bg-zinc-700 rounded px-3 py-1 text-center shrink-0`}>
                                    {tier.quantity}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {tier.allClients.map((user, userIndex) => {
                                      const hasPosition = position.clientPositionMap?.[user.client_id]?.length > 0;
                                      
                                      // Get actual quantities for this client
                                      const actualQuantities = position.clientPositionMap?.[user.client_id] || [];
                                      
                                      // Check if any actual quantity matches the tier quantity
                                      const quantityMatches = actualQuantities.some(qty => qty === tier.quantity);
                                      
                                      // Determine background color based on three conditions:
                                      // 1. No position: bg-zinc-700 (gray)
                                      // 2. Has position + quantity matches: bg-green-800/bg-rose-900 (green/red based on side)
                                      // 3. Has position + quantity mismatch: bg-blue-500 (blue)
                                      let bgColor = 'bg-zinc-700'; // Default: no position
                                      
                                      if (hasPosition) {
                                        if (quantityMatches) {
                                          // Position exists and quantity matches tier quantity
                                          bgColor = position.side === 1 
                                            ? 'bg-green-800 border border-green-500/30' 
                                            : 'bg-rose-900 border border-rose-500/30';
                                        } else {
                                          // Position exists but quantity doesn't match tier quantity
                                          bgColor = 'bg-amber-700 border border-amber-500/30';
                                        }
                                      }
                                      
                                      // Override if tier quantity is negative
                                      if (tier.quantity < 0) {
                                        bgColor = 'bg-rose-900 border border-rose-500/30';
                                      }
                                      
                                      return (
                                        <span 
                                          key={userIndex}
                                          className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${bgColor}`}
                                        >
                                          {hasPosition && (
                                            <Check className="w-3 h-3 text-white" />
                                          )}
                                          <span>{user.name}</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-red-400">
                                {position.error || 'No multiplier tiers available'}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        // Stock Options - Current Logic
                        <>
                          <div className="bg-zinc-700 px-4 py-2">
                            <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
                              <div>Quantity</div>
                              <div>Clients</div>
                            </div>
                          </div>
                          
                          <div className="divide-y divide-zinc-700">
                            {position.quantityTiers.map((tier, tierIndex) => (
                              <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                                <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                  {tier.quantity}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {accountNames
                                    .filter(user => tier.clientIds.includes(user.client_id))
                                    .map((user, userIndex) => {
                                      const hasPosition = tier.clientIds.includes(user.client_id);
                                      return (
                                        <span 
                                          key={userIndex}
                                          className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                            hasPosition && position.side === 1 ? 'bg-green-900 border border-green-500/30' :  position.side !== 1 ? 'bg-rose-900 border-rose-500/30' :'bg-zinc-700'
                                          }
                                          ${tier.quantity < 0 ? 'bg-rose-900 border-rose-500/30' : ''}
                                          `}
                                        >
                                          {hasPosition && (
                                            <Check className="w-3 h-3 text-white" />
                                          )}
                                          <span>{user.name}</span>
                                        </span>
                                      );
                                    })
                                  }
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>



      

      {/* Modals */}
      {showDriftModal && (
        <DriftModal
          currentPositionData={currentPositionData}
          handleCloseDriftModal={handleCloseDriftModal}
          handlePositionDrift={handlePositionDrift}
          isLoading={isLoading}
        />
      )}

      {showTPModal && (
        <TPModal
          currentPositionData={currentPositionData}
          handleCloseTPModal={handleCloseTPModal}
          handlePositionTP={handlePositionTP}
          isLoading={isLoading}
        />
      )}

      {showExitModal && (
        <ExitPositionModal
          currentPositionData={currentPositionData}
          handleCloseExitModal={handleCloseExitModal}
          handleExitPosition={handleExitPosition}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
























































































// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";
// import apiClient from "../../utils/apiClient";
// import toast from "react-hot-toast";

// // Helper function to get symbol-specific multiplier and rounding unit
// const getSymbolConfig = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
  
//   if (upperSymbol.includes('BANKNIFTY')) {
//     return {
//       multiplierField: 'banknifty_multiplier',
//       roundingUnit: 35,
//       symbolType: 'BANKNIFTY'
//     };
//   } else if (upperSymbol.includes('FINNIFTY')) {
//     return {
//       multiplierField: 'finnifty_multiplier',
//       roundingUnit: 65,
//       symbolType: 'FINNIFTY'
//     };
//   } else if (upperSymbol.includes('MIDCPNIFTY')) {
//     return {
//       multiplierField: 'midcpnifty_multiplier',
//       roundingUnit: 20,
//       symbolType: 'MIDCPNIFTY'
//     };
//   } else if (upperSymbol.includes('SENSEX')) {
//     return {
//       multiplierField: 'sensex_multiplier',
//       roundingUnit: 20,
//       symbolType: 'SENSEX'
//     };
//   } else if (upperSymbol.includes('NIFTY')) {
//     return {
//       multiplierField: 'index_multiplier',
//       roundingUnit: 75,
//       symbolType: 'NIFTY'
//     };
//   }
  
//   return null;
// };

// // Helper function to get base quantity for index positions
// const getIndexPositionBaseQuantity = (symbol, strategy, side, positionData = [], accountNames = []) => {
//   const referenceClientId = "CA00246";
//   const symbolConfig = getSymbolConfig(symbol);
  
//   if (!symbolConfig) {
//     console.log(`❌ Symbol "${symbol}" is not recognized as an index symbol`);
//     return null;
//   }
  
//   console.log(`\n=== DEBUGGING ${symbolConfig.symbolType} POSITION BASE CALCULATION ===`);
//   console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side}, Client="${referenceClientId}"`);
//   console.log(`📊 Symbol Config: Type=${symbolConfig.symbolType}, MultiplierField=${symbolConfig.multiplierField}, RoundingUnit=${symbolConfig.roundingUnit}`);
  
//   // Find reference client's multiplier from accountNames
//   console.log(`\n📋 Available Accounts:`);
//   accountNames.forEach(u => {
//     const multiplier = u[symbolConfig.multiplierField];
//     console.log(`  - ID: "${u.client_id}", Name: "${u.name}", ${symbolConfig.symbolType} Multiplier: ${multiplier}`);
//   });
  
//   const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
//   if (!referenceClientData) {
//     console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
//     console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
//     return null;
//   }
  
//   const referenceMultiplier = referenceClientData[symbolConfig.multiplierField];
//   if (!referenceMultiplier || referenceMultiplier <= 0) {
//     console.log(`❌ Reference client "${referenceClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${referenceMultiplier}`);
//     return null;
//   }
  
//   console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", ${symbolConfig.symbolType} Multiplier=${referenceMultiplier}`);
  
//   // Show ALL positions for this symbol
//   console.log(`\n📦 All positions for symbol "${symbol}":`);
//   const symbolPositions = positionData.filter(p => p.symbol === symbol);
//   symbolPositions.forEach((p, i) => {
//     console.log(`  [${i}] Strategy="${p.strategy}", Side=${p.side}, NetQty=${p.netQty}, ClientIDs=[${p.clientIds.join(', ')}]`);
//   });
  
//   // Find reference client's position quantity for this EXACT symbol + strategy + side combination
//   console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${side} + Client="${referenceClientId}"`);
  
//   const referencePosition = positionData.find(position => 
//     position.symbol === symbol && 
//     position.strategy === strategy && 
//     position.side === side && 
//     position.clientIds.includes(referenceClientId)
//   );
  
//   if (!referencePosition) {
//     console.log(`❌ NO EXACT MATCH FOUND for reference client "${referenceClientId}"!`);
//     console.log(`📦 Positions matching symbol+strategy+side (without client filter):`);
//     const partialMatches = positionData.filter(p => 
//       p.symbol === symbol && p.strategy === strategy && p.side === side
//     );
//     partialMatches.forEach(p => {
//       console.log(`  - NetQty=${p.netQty}, ClientIDs=[${p.clientIds.join(', ')}], Contains "${referenceClientId}"? ${p.clientIds.includes(referenceClientId)}`);
//     });
    
//     // 🔄 FALLBACK LOGIC: Use any position's netQty with that client's multiplier
//     if (partialMatches.length > 0) {
//       const fallbackPosition = partialMatches[0]; // Use first matching position
//       const fallbackClientId = fallbackPosition.clientIds[0]; // Use first client from the position
      
//       console.log(`🔄 FALLBACK: Using position netQty with client's multiplier`);
//       console.log(`📦 Fallback position: NetQty=${fallbackPosition.netQty}, ClientIDs=[${fallbackPosition.clientIds.join(', ')}]`);
//       console.log(`👤 Using fallback client: "${fallbackClientId}"`);
      
//       // Find the fallback client's multiplier
//       const fallbackClientData = accountNames.find(user => user.client_id === fallbackClientId);
//       if (!fallbackClientData) {
//         console.log(`❌ Fallback client "${fallbackClientId}" NOT FOUND in accountNames`);
//         return null;
//       }
      
//       const fallbackMultiplier = fallbackClientData[symbolConfig.multiplierField];
//       if (!fallbackMultiplier || fallbackMultiplier <= 0) {
//         console.log(`❌ Fallback client "${fallbackClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${fallbackMultiplier}`);
//         return null;
//       }
      
//       console.log(`✅ Found fallback client: ID="${fallbackClientData.client_id}", Name="${fallbackClientData.name}", ${symbolConfig.symbolType} Multiplier=${fallbackMultiplier}`);
      
//       const fallbackQty = fallbackPosition.netQty;
      
//       // Calculate base quantity using the fallback client's multiplier
//       const rawFallbackBaseQty = fallbackQty / fallbackMultiplier;
      
//       // Round the base quantity to nearest multiple of symbol-specific rounding unit
//       const fallbackBaseQty = Math.round(rawFallbackBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
      
//       console.log(`\n📊 FALLBACK CALCULATION:`);
//       console.log(`   NetQty: ${fallbackQty}`);
//       console.log(`   ${symbolConfig.symbolType} Multiplier (${fallbackClientId}): ${fallbackMultiplier}`);
//       console.log(`   Raw Base: ${fallbackQty} ÷ ${fallbackMultiplier} = ${rawFallbackBaseQty}`);
//       console.log(`   Rounded Base: ${rawFallbackBaseQty} → ${fallbackBaseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
//       console.log(`🎉 FALLBACK BASE QUANTITY: ${fallbackBaseQty}`);
//       console.log(`=== END ${symbolConfig.symbolType} POSITION DEBUGGING (FALLBACK) ===\n`);
      
//       return fallbackBaseQty;
//     }
    
//     console.log(`🔄 NO FALLBACK POSSIBLE - No positions found for symbol+strategy+side combination`);
//     return null;
//   }
  
//   console.log(`✅ FOUND EXACT MATCH!`);
//   console.log(`📦 Position details: Strategy="${referencePosition.strategy}", Side=${referencePosition.side}, NetQty=${referencePosition.netQty}`);
//   console.log(`👥 ClientIDs in this position: [${referencePosition.clientIds.join(', ')}]`);
  
//   const referenceQty = referencePosition.netQty;
  
//   // Calculate base quantity using the reference client's actual multiplier
//   const rawBaseQty = referenceQty / referenceMultiplier;
  
//   // Round the base quantity to nearest multiple of symbol-specific rounding unit
//   const baseQty = Math.round(rawBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
  
//   console.log(`\n📊 CALCULATION:`);
//   console.log(`   NetQty: ${referenceQty}`);
//   console.log(`   ${symbolConfig.symbolType} Multiplier: ${referenceMultiplier}`);
//   console.log(`   Raw Base: ${referenceQty} ÷ ${referenceMultiplier} = ${rawBaseQty}`);
//   console.log(`   Rounded Base: ${rawBaseQty} → ${baseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
//   console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
//   console.log(`=== END ${symbolConfig.symbolType} POSITION DEBUGGING ===\n`);
  
//   return baseQty;
// };

// // Helper function to calculate expected quantity for any index symbol positions
// const calculateIndexPositionExpectedQuantity = (baseQty, targetMultiplier, roundingUnit) => {
//   // Calculate expected quantity
//   const expectedQty = baseQty * targetMultiplier;
  
//   // Round to nearest multiple of symbol-specific rounding unit
//   const roundedQty = Math.round(expectedQty / roundingUnit) * roundingUnit;
  
//   console.log(`Index position calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty} (unit: ${roundingUnit})`);
  
//   return roundedQty;
// };

// // Helper function to check if symbol is index option
// const isIndexOption = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
//   return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY') || upperSymbol.includes('FINNIFTY') || upperSymbol.includes('SENSEX') || upperSymbol.includes('MIDCPNIFTY');
// };


// // Enhanced Modal Components
// const DriftModal = ({isLoading, currentPositionData, handleCloseDriftModal, handlePositionDrift }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Drift Position</h3>
//             <p className="text-sm text-zinc-400">Execute drift strategy</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseDriftModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Side:</span>
//               <p className={`font-medium ${currentPositionData?.side === 1 ? 'text-green-400' : 'text-red-400'}`}>
//                 {currentPositionData?.side === 1 ? 'BUY' : 'SELL'}
//               </p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Net Qty:</span>
//               <p className="text-white font-medium">{currentPositionData?.netQty}</p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
//           <p className="text-amber-200 text-sm">
//             ⚠️ This action will execute the drift strategy for this position. Please confirm to proceed.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseDriftModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//           disabled={isLoading}
//           onClick={handlePositionDrift}
//           className={`disabled:cursor-not-allowed disabled:bg-violet-600/20 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors shadow-lg`}
//         >
//           Execute Drift
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const TPModal = ({isLoading, currentPositionData, handleCloseTPModal, handlePositionTP }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Take Profit</h3>
//             <p className="text-sm text-zinc-400">Execute take profit strategy</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseTPModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Current P&L:</span>
//               <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
//                 ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
//               </p>
//             </div>
//             <div>
//               <span className="text-zinc-400">LTP:</span>
//               <p className="text-white font-medium">₹{currentPositionData?.ltp?.toFixed(2)}</p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
//           <p className="text-green-200 text-sm">
//             📈 This will execute the take profit strategy and potentially close profitable positions.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseTPModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//           disabled={isLoading}
//           onClick={handlePositionTP}
//           className="disabled:cursor-not-allowed disabled:bg-amber-600/20 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors shadow-lg"
//         >
//           Execute TP
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const ExitPositionModal = ({isLoading, currentPositionData, handleCloseExitModal, handleExitPosition }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-rose-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Exit Position</h3>
//             <p className="text-sm text-zinc-400">Close entire position</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseExitModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Net Qty:</span>
//               <p className="text-white font-medium">{currentPositionData?.netQty}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Final P&L:</span>
//               <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
//                 ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
//               </p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
//           <p className="text-red-200 text-sm">
//             ⚠️ This action will close the entire position immediately. This action cannot be undone.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseExitModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//          disabled={isLoading}
//           onClick={handleExitPosition}
//           className="disabled:cursor-not-allowed disabled:bg-rose-600/20 px-6 py-2 bg-rose-600 hover:bg-rose-500 text-white font-medium rounded-lg transition-colors shadow-lg"
//         >
//           Exit Position
//         </button>
//       </div>
//     </div>
//   </div>
// );

// export default function NewLivePositionsRestAccounts({
//   filteredLivePositionsData = [],
//   setFilteredLivePositionsFno,
//   setFilteredLivePositionsEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   ...props
// }) {
//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showDriftModal, setShowDriftModal] = useState(false);
//   const [showTPModal, setShowTPModal] = useState(false);
//   const [showExitModal, setShowExitModal] = useState(false);
//   const [currentPositionData, setCurrentPositionData] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
  

//   console.log("AccountNames data:", accountNames);
//   console.log("Position symbol detection test:", filteredLivePositionsData.map(position => ({
//     symbol: position.symbol,
//     strategy: position.strategy,
//     side: position.side,
//     isIndex: isIndexOption(position.symbol),
//     symbolConfig: getSymbolConfig(position.symbol),
//     netQty: position.netQty
//   })));

//   // Group positions by symbol + strategy + side
//   const groupedPositions = useMemo(() => {
//     const groups = {};
    
//     filteredLivePositionsData.forEach(position => {
//       const key = `${position.symbol}-${position.strategy}-${position.side}`;
      
//       if (!groups[key]) {
//         groups[key] = {
//           symbol: position.symbol,
//           strategy: position.strategy,
//           side: position.side,
//           netAvg: position.netAvg,
//           ltp: position.ltp,
//           pl: position.pl,
//           actualQuantities: {}, // Store actual quantities from positions
//           clientPositionMap: {} // Map client to their actual quantities
//         };
//       }
      
//       // Store actual quantities and client mappings
//       position.clientIds.forEach(clientId => {
//         if (!groups[key].clientPositionMap[clientId]) {
//           groups[key].clientPositionMap[clientId] = [];
//         }
//         groups[key].clientPositionMap[clientId].push(position.netQty);
//       });
      
//       if (!groups[key].actualQuantities[position.netQty]) {
//         groups[key].actualQuantities[position.netQty] = [];
//       }
//       groups[key].actualQuantities[position.netQty].push(...position.clientIds);
//     });
    
//     // Remove duplicates from clientPositionMap
//     Object.values(groups).forEach(group => {
//       Object.keys(group.clientPositionMap).forEach(clientId => {
//         group.clientPositionMap[clientId] = [...new Set(group.clientPositionMap[clientId])];
//       });
//     });
    
//     return Object.values(groups);
//   }, [filteredLivePositionsData]);

//   // Generate multiplier tiers - Dynamic multipliers for all index symbols
//   const enhancedGroupedPositions = useMemo(() => {
//     return groupedPositions.map(group => {
//       const isIndex = isIndexOption(group.symbol);
      
//       if (isIndex) {
//         const symbolConfig = getSymbolConfig(group.symbol);
        
//         if (!symbolConfig) {
//           console.log(`❌ Unknown index symbol: ${group.symbol}`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `Unknown index symbol: ${group.symbol}`
//           };
//         }
        
//         // Use unified base quantity calculation for all index symbols
//         const baseQty = getIndexPositionBaseQuantity(group.symbol, group.strategy, group.side, filteredLivePositionsData, accountNames);
        
//         if (!baseQty) {
//           console.log(`Could not determine base quantity for ${group.symbol} + ${group.strategy} + ${group.side}`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `No valid base quantity found for ${group.symbol} + ${group.strategy} + ${group.side}`
//           };
//         }
        
//         console.log(`Processing ${symbolConfig.symbolType} position ${group.symbol} (${group.strategy}, side ${group.side}) with CA00246 base quantity: ${baseQty}`);
        
//         // Get unique multipliers from accountNames for this specific symbol type
//         const uniqueMultipliers = [...new Set(
//           accountNames
//             .map(user => user[symbolConfig.multiplierField])
//             .filter(multiplier => multiplier && multiplier > 0)
//         )].sort((a, b) => a - b);
        
//         if (uniqueMultipliers.length === 0) {
//           console.log(`❌ No valid multipliers found for ${symbolConfig.symbolType}`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `No valid ${symbolConfig.symbolType} multipliers found`
//           };
//         }
        
//         const multiplierTiers = uniqueMultipliers.map(multiplier => {
//           // Calculate expected quantity using symbol-specific logic
//           const expectedQty = calculateIndexPositionExpectedQuantity(baseQty, multiplier, symbolConfig.roundingUnit);
          
//           // Get clients who actually have this quantity (or closest to it)
//           const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
          
//           // Also check for clients with quantities close to expected (within 10% tolerance for rounding)
//           const tolerance = expectedQty * 0.1;
//           const clientsWithSimilarQty = [];
          
//           Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
//             const actualQty = parseInt(qty);
//             if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
//               clientsWithSimilarQty.push(...clientIds);
//             }
//           });
          
//           const allClientsWithPosition = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
          
//           return {
//             multiplier,
//             quantity: expectedQty,
//             expectedQuantity: expectedQty,
//             allClients: accountNames.filter(user => user[symbolConfig.multiplierField] === multiplier),
//             clientsWithPosition: allClientsWithPosition
//           };
//         });
        
//         return {
//           ...group,
//           multiplierTiers,
//           isIndexOption: true,
//           baseQuantity: baseQty,
//           symbolConfig: symbolConfig
//         };
//       } else {
//         // For stock options, keep current logic
//         const quantityTiers = Object.entries(group.actualQuantities)
//           .map(([qty, clientIds]) => ({
//             quantity: parseInt(qty),
//             clientIds: [...new Set(clientIds)]
//           }))
//           .sort((a, b) => a.quantity - b.quantity);
          
//         return {
//           ...group,
//           quantityTiers,
//           isIndexOption: false
//         };
//       }
//     });
//   }, [groupedPositions, accountNames, filteredLivePositionsData]);

//   console.log("Enhanced grouped positions (Dynamic multipliers for all index symbols):", enhancedGroupedPositions);

//   // Search and sorting logic (Type 2 search - reorder, don't filter)
//   const searchedAndSortedPositions = useMemo(() => {
//     let result = [...enhancedGroupedPositions];
    
//     // Apply sorting first if configured
//     if (sortConfig.key) {
//       result.sort((a, b) => {
//         let aValue = a[sortConfig.key];
//         let bValue = b[sortConfig.key];
        
//         // Handle different data types
//         if (sortConfig.key === 'symbol') {
//           aValue = aValue.replace("NSE:", "").toLowerCase();
//           bValue = bValue.replace("NSE:", "").toLowerCase();
//         } else if (sortConfig.key === 'side') {
//           aValue = aValue === 1 ? 'buy' : 'sell';
//           bValue = bValue === 1 ? 'buy' : 'sell';
//         } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
//           aValue = parseFloat(aValue) || 0;
//           bValue = parseFloat(bValue) || 0;
//         }
        
//         if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//         if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//         return 0;
//       });
//     }
    
//     // Then apply search reordering (matching items go to top while maintaining sort order within each group)
//     if (searchTerm.trim()) {
//       const searchLower = searchTerm.toLowerCase().trim();
      
//       // Separate matching and non-matching positions while preserving their sorted order
//       const matchingPositions = result.filter(position => 
//         position.symbol.toLowerCase().includes(searchLower)
//       );
//       const nonMatchingPositions = result.filter(position => 
//         !position.symbol.toLowerCase().includes(searchLower)
//       );
      
//       // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
//       result = [...matchingPositions, ...nonMatchingPositions];
//     }
    
//     return result;
//   }, [enhancedGroupedPositions, searchTerm, sortConfig]);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handleSearchChange = (e) => {
//     setSearchTerm(e.target.value);
//   };

//   const clearSearch = () => {
//     setSearchTerm('');
//   };

//   const getSideColor = (side) => {
//     return side === 1 ? "text-green-400" : "text-red-400";
//   };

//   const getPLColor = (pl) => {
//     const plValue = parseFloat(pl);
//     if (plValue > 0) return "text-green-400";
//     if (plValue < 0) return "text-red-400";
//     return "text-white";
//   };

//   const getSortIcon = (columnKey) => {
//     if (sortConfig.key !== columnKey) {
//       return <ChevronsUpDown className="w-4 h-4" />;
//     }
//     return sortConfig.direction === 'asc' ? 
//       <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
//       <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
//   };

//   // Modal handlers
//   const handleCloseDriftModal = () => setShowDriftModal(false);
//   const handleCloseTPModal = () => setShowTPModal(false);
//   const handleCloseExitModal = () => setShowExitModal(false);

//   const handleOpenDriftModal = (position) => {
//     setCurrentPositionData(position);
//     setShowDriftModal(true);
//   };

//   const handleOpenTPModal = (position) => {
//     setCurrentPositionData(position);
//     setShowTPModal(true);
//   };

//   const handleOpenExitModal = (position) => {
//     setCurrentPositionData(position);
//     setShowExitModal(true);
//   };

//   const handlePositionDrift = () => {
//     setIsLoading(true);
//      if (currentPositionData !== null) {

//     const clientIds = {};
    
//     Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
//       // Get the quantity (first element of the array)
//       clientIds[clientId] = quantities[0];
//     });
    
//     // Create the restructured payload
//     const payload = {
//       symbol: currentPositionData.symbol,
//       side: currentPositionData.side,
//       netAvg: currentPositionData.netAvg,
//       ltp: currentPositionData.ltp,
//       strategy: currentPositionData.strategy,
//       pl: currentPositionData.pl,
//       clientIds: clientIds
//     };
    
//     console.log("Restructured Payload:", payload);
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/drift_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("Drift Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position Drifted Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position Drift Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseDriftModal();
//            setIsLoading(false);
//          })
//          .catch((error) => {
//            console.log("Error in position request:", error);
//            handleCloseDriftModal();
//            setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for Drift");
//        setIsLoading(false);
//      }
//    };
 
//    const handlePositionTP = () => {
//       setIsLoading(true);
//      if (currentPositionData !== null) {

//     // Convert clientPositionMap to clientIds object
//     const clientIds = {};
    
//     Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
//       clientIds[clientId] = quantities[0];
//     });
    
//     // Create the restructured payload
//     const payload = {
//       symbol: currentPositionData.symbol,
//       side: currentPositionData.side,
//       netAvg: currentPositionData.netAvg,
//       ltp: currentPositionData.ltp,
//       strategy: currentPositionData.strategy,
//       pl: currentPositionData.pl,
//       clientIds: clientIds
//     };
    
//     console.log("Restructured Payload:", payload);
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/tp_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("TP Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position TP Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position TP Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseTPModal();
//            setIsLoading(false);
//          })
//          .catch((error) => {
//            console.log("Error in position request:", error);
//            handleCloseTPModal();
//            setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for TP");
//        setIsLoading(false);
//      }
//    };
 
//    const handleExitPosition = () => {
//      setIsLoading(true);
//      if (currentPositionData !== null) {
     
//     // Convert clientPositionMap to clientIds object
//     const clientIds = {};
    
//     Object.entries(currentPositionData.clientPositionMap).forEach(([clientId, quantities]) => {
//       // Get the quantity (first element of the array)
//       clientIds[clientId] = quantities[0];
//     });
    
//     // Create the restructured payload
//     const payload = {
//       symbol: currentPositionData.symbol,
//       side: currentPositionData.side,
//       netAvg: currentPositionData.netAvg,
//       ltp: currentPositionData.ltp,
//       strategy: currentPositionData.strategy,
//       pl: currentPositionData.pl,
//       clientIds: clientIds
//     };
    
//     console.log("Restructured Payload:", payload);
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/exit_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("Exit Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position Exit Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position Exit Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseExitModal();
//            setIsLoading(false);
           
//          })
//          .catch((error) => {
//            console.log("Error in exit position request:", error);
//            handleCloseExitModal();
//            setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for exit");
//        setIsLoading(false);
//      }
//    };

//   if (!filteredLivePositionsData || filteredLivePositionsData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Live Position Found!!</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
//       {/* Search Bar */}
//       <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
//         <div className="relative max-w-md">
//           <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
//           <input
//             type="text"
//             placeholder="Search by symbol..."
//             value={searchTerm}
//             onChange={handleSearchChange}
//             className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//           {searchTerm && (
//             <button
//               onClick={clearSearch}
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
//             >
//               <X className="w-4 h-4" />
//             </button>
//           )}
//         </div>

//          {/* Add filter Here  */}
        
//       </div>

//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-10 gap-4 px-6 py-1 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center col-span-2 space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             {getSortIcon('symbol')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('side')}
//           >
//             <span>Buy/Sell</span>
//             {getSortIcon('side')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('netQty')}
//           >
//             <span>Net Qty</span>
//             {getSortIcon('netQty')}
//           </button>

//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('netAvg')}
//           >
//             <span>Avg Price</span>
//             {getSortIcon('netAvg')}
//           </button>

//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('ltp')}
//           >
//             <span>LTP</span>
//             {getSortIcon('ltp')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             {getSortIcon('strategy')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('pl')}
//           >
//             <span>Total P&L</span>
//             {getSortIcon('pl')}
//           </button>
          
//           <div className="text-left col-span-2">Actions</div>
//         </div>
//       </div>

//       {/* Scrollable Table Body - No Pagination, All Results Shown */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {searchedAndSortedPositions.map((position, index) => (
//               <div key={index} className={` ${
//                 searchTerm && position.symbol.toLowerCase().includes(searchTerm.toLowerCase()) 
//                   ? ' bg-[#07F2C7]/50' 
//                   : 'bg-zinc-900'
//               }`}>
//                 {/* Main Position Row */}
//                 <div className="grid grid-cols-10 gap-4 px-6 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
//                   <div className="font-medium col-span-2">
//                     {position.symbol}
//                   </div>
//                   <div className={`font-medium ${getSideColor(position.side)}`}>
//                     {position.side === 1 ? "BUY" : "SELL"}
//                   </div>
//                   <div>{position.isIndexOption ? 
//                     position.multiplierTiers?.reduce((sum, tier) => sum + (tier.clientsWithPosition.length * tier.quantity), 0) :
//                     position.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
//                   }</div>
//                   <div>₹{position.netAvg?.toFixed(2)}</div>
//                   <div>₹{position.ltp !== 'N/A' ? position.ltp?.toFixed(2) : 'N/A'}</div>
//                   <div className="font-medium">{position.strategy}</div>
//                   <div className={`font-medium ${getPLColor(position.pl)}`}>
//                     ₹{parseFloat(position.pl)?.toFixed(2)}
//                   </div>
//                   <div className="flex items-center space-x-2 col-span-2">
//                     <button
//                       onClick={() => handleOpenDriftModal(position)}
//                       className="bg-violet-800 cursor-pointer hover:bg-violet-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       Drift
//                     </button>
//                     <button
//                       onClick={() => handleOpenTPModal(position)}
//                       className="bg-amber-800 cursor-pointer hover:bg-amber-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       TP
//                     </button>
//                     <button
//                       onClick={() => handleOpenExitModal(position)}
//                       className="bg-rose-800 cursor-pointer hover:bg-rose-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       Exit
//                     </button>
//                   </div>
//                 </div>

//                 {/* Enhanced Quantity-Client Sub Table */}
//                 <div className="px-6 pb-4">
//                   <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
//                     {position.isIndexOption ? (
//                       // Index Options - Dynamic multipliers for all index symbols
//                       <>
//                         <div className="bg-zinc-700 px-5 py-2">
//                           <div className="flex gap-4 text-xs font-medium text-zinc-300">
//                             <div className="min-w-[125px] flex items-start justify-start">
//                               {position.symbolConfig ? `${position.symbolConfig.symbolType} Multiplier` : 'Index Multiplier'}
//                             </div>
//                             <div className="min-w-[125px] flex items-start justify-start">Expected Qty</div>
//                             <div className="min-w-[140px] flex items-start justify-start">Clients</div>
//                             {position.baseQuantity && position.symbolConfig && (
//                               <div className="min-w-[180px] flex items-start justify-start text-amber-400">
//                                 {`Base: ${position.baseQuantity} (CA00246 + Strategy, Unit: ${position.symbolConfig.roundingUnit})`}
//                               </div>
//                             )}
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {position.multiplierTiers && position.multiplierTiers.length > 0 ? (
//                             position.multiplierTiers.map((tier, tierIndex) => (
//                               <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                                 <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                   {tier.multiplier}
//                                 </div>
//                                 <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                   {tier.quantity}
//                                 </div>
//                                 <div className="flex flex-wrap gap-2">
//                                   {tier.allClients.map((user, userIndex) => {
//                                     const hasPosition = tier.clientsWithPosition.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasPosition && position.side === 1 ? 'bg-green-800 border border-green-500/30' : position.side !== 1 ? 'bg-zinc-700' : ''
//                                         }`}
//                                       >
//                                         {hasPosition && (
//                                           <Check className="w-3 h-3 text-white" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })}
//                                 </div>
//                               </div>
//                             ))
//                           ) : (
//                             <div className="px-4 py-3 text-sm text-red-400">
//                               {position.error || 'No multiplier tiers available'}
//                             </div>
//                           )}
//                         </div>
//                       </>
//                     ) : (
//                       // Stock Options - Current Logic
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
//                             <div>Quantity</div>
//                             <div>Clients</div>
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {position.quantityTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {accountNames
//                                   .filter(user => tier.clientIds.includes(user.client_id))
//                                   .map((user, userIndex) => {
//                                     const hasPosition = tier.clientIds.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasPosition && position.side === 1 ? 'bg-green-900 border border-green-500/30' :  position.side !== 1 ? 'bg-zinc-700' :''
//                                         }`}
//                                       >
//                                         {hasPosition && (
//                                           <Check className="w-3 h-3 text-white" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })
//                                 }
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Modals */}
//       {showDriftModal && (
//         <DriftModal
//           currentPositionData={currentPositionData}
//           handleCloseDriftModal={handleCloseDriftModal}
//           handlePositionDrift={handlePositionDrift}
//           isLoading={isLoading}
//         />
//       )}

//       {showTPModal && (
//         <TPModal
//           currentPositionData={currentPositionData}
//           handleCloseTPModal={handleCloseTPModal}
//           handlePositionTP={handlePositionTP}
//           isLoading={isLoading}
//         />
//       )}

//       {showExitModal && (
//         <ExitPositionModal
//           currentPositionData={currentPositionData}
//           handleCloseExitModal={handleCloseExitModal}
//           handleExitPosition={handleExitPosition}
//           isLoading={isLoading}
//         />
//       )}
//     </div>
//   );
// }






